/**
 * @file TableContextMenu.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-17
 * @modified 2026-10-21
 * @description Right-click context menu for data tables. Driven by the
 *              useTableContextMenu hook, which owns the open/position/selection
 *              state; this component only renders. Portalled to document.body so
 *              a table's overflow:auto can't clip it, and clamped to the
 *              viewport off its measured size rather than a guessed one.
 *
 *              Three action groups, rendered in this order with dividers between
 *              whichever are non-empty:
 *                rowActions(row)         — acts on the right-clicked row
 *                bulkActions(ids, rows)  — only when >1 row is selected
 *                actions                 — generic, always available
 *
 *              Action shape (matches RowActionsMenu so a table can reuse the
 *              same builder for both surfaces):
 *                { key?, icon, label, onClick, disabled?, active?, title?, className? }
 *              className reaches the item, as in RowActionsMenu (e.g.
 *              action-warning: an action that will explain a problem).
 *              Falsy entries are dropped, so `cond && {...}` is fine inline.
 *
 *              bulkTitle (1.2.0, Cam 19 Oct — "clearly indicate to user that this
 *              is a bulk action"): a string, or (ids, rows) => string. Given, a
 *              right-click on one of several ticked rows shows that heading over
 *              the bulk actions and leaves the single row's actions out, so
 *              nothing in the menu reads as acting on one row. Without it the
 *              menu is as before.
 */

import useReadOnly from '../hooks/useReadOnly';
import { applyReadOnly } from '../utils/readOnlyActions';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placeVertically } from '../utils/menuPlacement';
import './TableContextMenu.css';

const VIEWPORT_PAD = 8;

function MenuItem({ action, onRun }) {
  const { icon, label, disabled, active, title, className } = action;
  return (
    <button
      type="button"
      role="menuitem"
      className={`tcm-item${disabled ? ' disabled' : ''}${active ? ' active' : ''}${className ? ` ${className}` : ''}`}
      disabled={disabled}
      title={title || label}
      onClick={() => { if (!disabled) onRun(action); }}
    >
      <span className="material-symbols-rounded">{icon}</span>
      <span className="tcm-label">{label}</span>
      {active && <span className="material-symbols-rounded tcm-check">check</span>}
    </button>
  );
}

function TableContextMenu({ ctx, rowActions, bulkActions, actions = [], className = '', bulkTitle = null }) {
  // Default-deny for view-only accounts. Applied to all three groups here
  // rather than at each caller — see utils/readOnlyActions.js.
  const readOnly = useReadOnly();
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: ctx.x, top: ctx.y, ready: false });

  const { open, x, y, row, bulk, ids, rows, close } = ctx;

  // Measure once mounted, then clamp so the menu never spills off-screen.
  useLayoutEffect(() => {
    if (!open) { setPos(p => (p.ready ? { ...p, ready: false } : p)); return; }
    const el = menuRef.current;
    const w = el ? el.offsetWidth : 0;
    const h = el ? el.offsetHeight : 0;
    setPos({
      left: Math.max(VIEWPORT_PAD, Math.min(x, window.innerWidth - w - VIEWPORT_PAD)),
      // Below the pointer when it fits above the status bar, else flipped
      // above it (utils/menuPlacement, Cam 21 Oct 2026).
      top: placeVertically({ anchorTop: y, anchorBottom: y, height: h, pad: VIEWPORT_PAD }),
      ready: true
    });
  }, [open, x, y]);

  if (!open) return null;

  const group = (list) => (list || []).filter(Boolean);
  const ro = (list) => applyReadOnly(list, readOnly);
  const heading = bulk && bulkTitle ? (typeof bulkTitle === 'function' ? bulkTitle(ids, rows) : bulkTitle) : null;
  const rowGroup = row && !heading ? group(ro(rowActions ? rowActions(row) : [])) : [];
  const bulkGroup = bulk ? group(ro(bulkActions ? bulkActions(ids, rows) : [])) : [];
  const genericGroup = group(ro(typeof actions === 'function' ? actions() : actions));

  const groups = [rowGroup, bulkGroup, genericGroup].filter(g => g.length > 0);
  if (groups.length === 0) return null;

  const run = (action) => { close(); if (action.onClick) action.onClick(); };

  return createPortal(
    <div
      className="tcm-overlay"
      onClick={close}
      onContextMenu={(e) => { e.preventDefault(); close(); }}
    >
      <div
        ref={menuRef}
        role="menu"
        className={`table-context-menu${pos.ready ? ' ready' : ''}${className ? ` ${className}` : ''}`}
        style={{ left: pos.left, top: pos.top }}
        onClick={(e) => e.stopPropagation()}
      >
        {groups.map((g, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && <div className="tcm-divider" />}
            {heading && g === bulkGroup && (
              <div className="tcm-heading" role="presentation">
                <span className="material-symbols-rounded">library_add_check</span>{heading}
              </div>
            )}
            {g.map((a, i) => (
              <MenuItem key={a.key || `${gi}-${i}`} action={a} onRun={run} />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>,
    document.body
  );
}

export default TableContextMenu;
